import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, User, MessageSquare, Plane, Users, Calendar, Heart } from "lucide-react";
import employeeDashboard from "@/assets/demo-employee-dashboard.png";
import employeeExplore from "@/assets/demo-employee-explore.png";
import employeeTrips from "@/assets/demo-employee-trips.png";
import employeeMessages from "@/assets/demo-employee-messages.png";
import employeeCommunity from "@/assets/demo-employee-community.png";

const EmployeeCarouselSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Tableau de bord personnel",
      description: "Solde de points, demandes en cours, prochains voyages... Tout centralisé dans une interface intuitive.",
      image: employeeDashboard,
      icon: User,
      features: ["Solde Nest Points", "Statut des demandes", "Prochaines destinations"]
    },
    {
      title: "Exploration des destinations",
      description: "Découvrez des destinations adaptées au télétravail avec photos, coûts et avis de la communauté.",
      image: employeeExplore,
      icon: Plane,
      features: ["Destinations exclusives", "Avis communauté", "Filtres personnalisés"]
    },
    {
      title: "Gestion de vos voyages",
      description: "Planifiez, suivez et partagez vos expériences de télétravail avec un outil complet.",
      image: employeeTrips,
      icon: Calendar,
      features: ["Planning voyages", "Documents voyage", "Suivi en temps réel"]
    },
    {
      title: "Messagerie sécurisée",
      description: "Échangez avec les équipes RENEST et la communauté locale en toute sécurité.",
      image: employeeMessages,
      icon: MessageSquare,
      features: ["Support 24/7", "Conseils locaux", "Chat sécurisé"]
    },
    {
      title: "Communauté nomade",
      description: "Rejoignez une communauté de télétravailleurs et partagez vos expériences uniques.",
      image: employeeCommunity,
      icon: Users,
      features: ["Réseau global", "Événements locaux", "Partage d'expériences"]
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section className="py-24 px-4 bg-background">
      <div className="container mx-auto">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className="space-y-8 order-1 lg:order-1">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    {React.createElement(slides[currentSlide].icon, { className: "h-6 w-6 text-primary" })}
                  </div>
                  <Badge variant="outline" className="text-sm">
                    {currentSlide + 1} / {slides.length}
                  </Badge>
                </div>
                
                <h3 className="text-3xl font-bold text-foreground">
                  {slides[currentSlide].title}
                </h3>
                
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {slides[currentSlide].description}
                </p>
              </div>

              {/* Features */}
              <div className="space-y-3">
                {slides[currentSlide].features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <span className="text-foreground">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Slide indicators */}
              <div className="flex gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    aria-label={`Aller à la diapositive ${index + 1}`}
                    className={`w-12 h-3 rounded-full transition-all duration-300 ${
                      index === currentSlide 
                        ? 'bg-primary' 
                        : 'bg-muted hover:bg-muted-foreground/20'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Image */}
            <div className="relative order-2 lg:order-2">
              <div className="relative bg-white rounded-2xl shadow-2xl border overflow-hidden">
                <img 
                  src={slides[currentSlide].image} 
                  alt={slides[currentSlide].title}
                  className="w-full h-auto transition-all duration-500"
                />
              </div>
              
              {/* Navigation arrows */}
              <Button
                variant="outline"
                size="sm"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm"
                onClick={prevSlide}
                aria-label="Diapositive précédente"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm"
                onClick={nextSlide}
                aria-label="Diapositive suivante"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmployeeCarouselSection;